import { bool } from "prop-types";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

import { useNoPhoneCodes } from "Flocknote/stateHooks";

import { usePostTwoFactorAuth } from "Login/TwoFA/API/queries";
import { use2FAState } from "Login/TwoFA/API/queryHooks";
import { useTwoFAContext } from "Login/TwoFA/TwoFA";
import { LAST_EMAIL_PHONE } from "Login/TwoFA/constants";
import {
  useAccentColor,
  useButtonTextColor,
  useIsLegacy,
} from "Login/TwoFA/stateHooks";

import FnrInput2 from "UI/FnrInput2";
import { emailIcon, phoneIcon } from "UI/FnrInput2/defaultIcons";
import fnrInputTypes from "UI/FnrInput2/fnrInputTypes";
import FnrTypeNSave from "UI/FnrTypeNSave";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import { useLocalStorage } from "Utilities/Hooks/useLocalStorage";
import inputCursorToEnd from "Utilities/inputCursorToEnd";
import stopEvents from "Utilities/stopEvents";

import NextButton from "../../NextButton";
import PromptTypography from "../../PromptTypography";

import { useFirstFactorVerbiage } from "./firstFactorHooks";

const TypeNSaveRelayByWidth = forwardRef(({ typeNSave, ...props }, ref) => {
  const isLegacy = useIsLegacy();
  const accentColor = useAccentColor();
  const buttonTextColor = useButtonTextColor();
  const [{ isLoading }, refetch] = use2FAState();
  const { firstFactor } = useTwoFAContext();
  const isDisabled = !firstFactor || isLoading;

  const handleSubmit = (e) => {
    stopEvents(e);
    if (isDisabled) return;
    refetch();
  };

  if (typeNSave)
    return (
      <FnrTypeNSave
        buttonColor={accentColor}
        buttonTextColor={buttonTextColor}
        isLegacy={isLegacy}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        buttonText="Next"
        ref={ref}
        {...props}
      />
    );

  return (
    <form onSubmit={handleSubmit}>
      <FnrVerticalGap>
        <FnrInput2 ref={ref} isLoading={isLoading} {...props} />
        <NextButton isLoading={isLoading} />
      </FnrVerticalGap>
    </form>
  );
});

TypeNSaveRelayByWidth.propTypes = { typeNSave: bool };

const getType = (value) => {
  if (/[a-z]|@/.test(value)) return fnrInputTypes.email;
  else if (/^[0-9]{1,11}/.test(value.replace(/-/g, ""))) {
    return fnrInputTypes.phone;
  }
  return fnrInputTypes.text;
};

const FirstFactor = () => {
  const {
    firstFactor,
    setFirstFactor: setFirstFactorRaw,
    variant,
    isWideView,
    stoutInputs,
    onInputChange,
    minHeight,
  } = useTwoFAContext();
  const noPhoneCodes = useNoPhoneCodes();

  const { isFetched } = usePostTwoFactorAuth({ variant });
  const [type, setType] = useState(
    noPhoneCodes ? fnrInputTypes.email : getType(firstFactor)
  );
  const accentColor = useAccentColor();
  const inputRef = useRef();
  const firstFactorVerbiage = useFirstFactorVerbiage();

  const setFirstFactor = useCallback(
    (value, automatedSet) => {
      if (!automatedSet && typeof onInputChange === "function")
        onInputChange(value);
      setFirstFactorRaw(value);
      const newType = noPhoneCodes ? fnrInputTypes.email : getType(value);
      setType(newType);
      if (type !== fnrInputTypes.phone && newType === fnrInputTypes.phone) {
        // if updating to phone, the type doesn't get set soon enough in FnrInput2. This is a bit of a hack, feel free to refactor.
        inputCursorToEnd(inputRef.current);
      }
    },
    [onInputChange, setFirstFactorRaw, noPhoneCodes, type]
  );
  const [storedLastFirstFactor] = useLocalStorage(LAST_EMAIL_PHONE);

  const [hasSetStored, setHasSetStored] = useState(false);
  useEffect(() => {
    // We only want to attempt this once so that the stored value does not overwrite the user's typing.
    if (storedLastFirstFactor?.value && !firstFactor && !hasSetStored) {
      setFirstFactor(storedLastFirstFactor.value, true);
    }
    setHasSetStored(true);
  }, [storedLastFirstFactor, firstFactor, hasSetStored, setFirstFactor]);

  const label = useMemo(() => {
    switch (type) {
      case fnrInputTypes.phone:
        return "Phone";
      case fnrInputTypes.email:
        return "Email";
      default:
        return "Phone or Email";
    }
  }, [type]);

  const icon = useMemo(() => {
    switch (type) {
      case fnrInputTypes.phone:
        return phoneIcon;
      case fnrInputTypes.email:
        return emailIcon;
      default:
        return "fa-sign-in fas";
    }
  }, [type]);

  const containerRef = useRef(null);

  return (
    <SFnrVerticalGap
      $minHeight={minHeight}
      data-cy="TwoFA_FirstFactor"
      ref={containerRef}
    >
      {firstFactorVerbiage && (
        <PromptTypography>{firstFactorVerbiage}</PromptTypography>
      )}
      <TypeNSaveRelayByWidth
        placeholder={label}
        data-cy="TwoFA_FirstFactor_Input"
        type={type}
        value={firstFactor}
        setValue={setFirstFactor}
        icon={icon}
        iconColor={accentColor}
        autofocus={isFetched ? 300 : false}
        aria-label="account-phone-or-email"
        size="lg"
        flexType
        focusColor={accentColor}
        typeNSave={isWideView}
        stout={stoutInputs}
        autoComplete="username"
        ref={inputRef}
      />
    </SFnrVerticalGap>
  );
};

const SFnrVerticalGap = styled(FnrVerticalGap)`
  min-height: ${({ $minHeight }) => $minHeight};
  justify-content: center;
`;

export default FirstFactor;
